// How the caption face measures (#239), for the segmenter's line widths and boxes: the browser's
// own measure of the face at the caption's size, in live's pixels. Its line widths matter only
// when a paragraph breaks lines or fits its box to them (#264's settings), and only then does it
// wait for the face. Without a canvas (a test's page) it reads live's own fallback: 0.45 of the
// size a character.
import { faceName, loadFaces } from "../fonts";
import { shownText } from "../text-style";
import { captionPadding, type CaptionStyle } from "./caption-style";
import type { CaptionMetrics } from "./segment";

/** Live's measure without a canvas: a character is 0.45 of the size wide. */
const CHAR_SHARE = 0.45;

/** The caption face's metrics; with `widths`, its face loaded first for the words it measures. */
export async function captionMetrics(
  style: CaptionStyle,
  words: readonly string[],
  widths: boolean,
): Promise<CaptionMetrics> {
  if (widths) {
    const text = shownText(words.join(" "), style.textCase);
    await loadFaces([{ family: style.font, weight: style.weight, italic: style.italic, text }]);
  }
  const pad = captionPadding(style);
  const extras = {
    size: style.size,
    padX: pad.x,
    padY: pad.y,
    stroke: style.stroke?.width ?? 0,
    shadowBlur: style.shadow?.blur ?? 0,
    lineHeight: style.lineHeight,
  };
  const context = canvasContext();
  if (context === null) {
    return {
      ...extras,
      width: (shown) => shown.length * (CHAR_SHARE * style.size + style.letterSpacing),
      ascent: style.size * 0.8,
      descent: style.size * 0.2,
    };
  }
  context.font = `${style.italic ? "italic " : ""}${style.weight} ${style.size}px "${faceName(style.font)}", sans-serif`;
  const sample = context.measureText("Mg");
  return {
    ...extras,
    // The style's letter spacing (live's pixels) after each character, as the renderer draws.
    width: (shown) => context.measureText(shown).width + Array.from(shown).length * style.letterSpacing,
    ascent: sample.fontBoundingBoxAscent || style.size * 0.8,
    descent: sample.fontBoundingBoxDescent || style.size * 0.2,
  };
}

function canvasContext(): CanvasRenderingContext2D | null {
  try {
    return document.createElement("canvas").getContext("2d");
  } catch {
    // A page with no canvas (a test's) measures by live's fallback.
    return null;
  }
}
